import React, { useCallback, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import moment from 'moment'
import { toast } from 'react-toastify'
import { Link, useHistory, useLocation } from 'react-router-dom'
import {
  Button,
  ButtonGroup,
  Card,
  CircularProgress,
  Grid,
  Icon,
} from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import ModelListPage, { FILTER_TYPE } from 'app/components/ModelListPage'
import FilterChip from 'app/components/ModelListPage/FilterChip'
import {
  PAGE_PARAM,
  SEARCH_PARAM,
} from 'app/components/ModelListPage/listQuery'
import HttpHandler, { getModelChangeEvent } from 'app/reusable/HttpHandler'
import { useIsUser, useUserInfo } from 'app/ducks/auth/hooks'
import { USER_TYPE } from 'app/ducks/auth/types'
import { useModal } from 'app/ducks/modal/hooks'
import useIsMobile from 'app/hooks/useIsMobile'
import ActivityClassRollCall from '../gym/ActivityClassRollCall'
import { categoryParam } from './modalityCategory'

const ReactEcharts = React.lazy(() => import('echarts-for-react'))

const APP = 'activities'
// amsm-backend's teacher area endpoints (`activities.teacher_area`): the
// class list answers in the generic list API's shape, so `ModelListPage`
// renders it as a "model" of its own; the indicators reload on the
// `INSIGHTS_MODEL` change event, fired when a roll call is saved.
export const CLASSES_MODEL = 'teacher-classes'
export const INSIGHTS_MODEL = 'teacher-area'

// Whose area (managers and administrators may pick another teacher) and the
// indicators' period live in the URL, next to `ModelListPage`'s own params.
export const TEACHER_PARAM = 'docente'
export const PERIOD_PARAM = 'periodo'
const PERIODS = [7, 30, 90]
const DEFAULT_PERIOD = 30

const useStyles = makeStyles(({ palette, spacing }) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  sectionTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  periods: { marginLeft: 'auto' },
  periodsMobile: { marginLeft: 0, width: '100%', '& > *': { flex: 1 } },
  tile: { padding: spacing(2), height: '100%' },
  tileLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 13,
    fontWeight: 600,
    color: palette.text.secondary,
    '& .material-icons': { fontSize: 18 },
  },
  tileValue: { margin: '8px 0 2px', fontSize: 26, fontWeight: 700 },
  tileHint: { fontSize: 12, color: palette.text.secondary },
  attention: { color: palette.error.main },
  card: { padding: spacing(2), height: '100%' },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: '0 0 12px',
    fontSize: 15,
    fontWeight: 700,
  },
  cardCount: {
    marginLeft: 'auto',
    fontSize: 12,
    fontWeight: 600,
    color: palette.text.secondary,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 0',
    borderBottom: `1px solid ${palette.divider}`,
    '&:last-child': { borderBottom: 0 },
  },
  itemText: { minWidth: 0, '& b': { display: 'block' } },
  muted: { color: palette.text.secondary, fontSize: 12 },
  empty: {
    padding: '24px 8px',
    textAlign: 'center',
    color: palette.text.secondary,
    fontSize: 13,
  },
  loading: { transition: 'opacity 150ms ease-in' },
  isLoading: { opacity: 0.5, pointerEvents: 'none' },
  // The table cells break anywhere by default; class names read better whole.
  classCell: { minWidth: 160, wordBreak: 'normal' },
  streak: { fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' },
  pendingCount: { color: palette.error.main, fontSize: 12 },
  nextSession: { fontWeight: 600, whiteSpace: 'nowrap' },
  inProgress: { color: palette.primary.main },
}))

// Backend week days are 0 = Monday … 6 = Sunday; moment's are 0 = Sunday.
const weekDayLabel = (day) =>
  moment()
    .day((day + 1) % 7)
    .format('ddd')

export const formatPercent = (value) =>
  value === null || value === undefined
    ? '—'
    : `${Number(value).toLocaleString('pt-BR', {
        maximumFractionDigits: 1,
      })}%`

const formatHours = (minutes) => {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (!hours) return rest ? `${rest}min` : '0h'
  return rest ? `${hours}h${String(rest).padStart(2, '0')}` : `${hours}h`
}

const formatSchedule = (row) =>
  [
    (row.week_days || []).map(weekDayLabel).join(', '),
    `${row.start_time}–${row.end_time}`,
  ]
    .filter(Boolean)
    .join(' · ')

/** "Agora · até 10:30", "Hoje · 18:00", "Amanhã · 18:00", "qui, 23/10 · 18:00". */
export const formatNextSession = (row, now = moment()) => {
  if (!row.next_session) return row.is_active ? 'Encerrada' : 'Inativa'
  if (row.is_in_progress) return `Agora · até ${row.end_time}`

  const next = moment.parseZone(row.next_session)
  const time = next.format('HH:mm')
  const day = next.format('YYYY-MM-DD')
  if (day === now.format('YYYY-MM-DD')) return `Hoje · ${time}`
  if (day === now.clone().add(1, 'day').format('YYYY-MM-DD'))
    return `Amanhã · ${time}`
  return `${next.format('ddd, DD/MM')} · ${time}`
}

const formatDate = (date) => moment(date).format('DD/MM/YYYY')

const periodRange = (days) => ({
  start_date: moment()
    .subtract(days - 1, 'days')
    .format('YYYY-MM-DD'),
  end_date: moment().format('YYYY-MM-DD'),
})

const useTeacherInsights = (params) => {
  const [insights, setInsights] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [hasFailed, setHasFailed] = useState(false)
  const key = JSON.stringify(params)

  const load = useCallback(() => {
    let cancelled = false
    setIsLoading(true)
    HttpHandler.teacherAreaInsights(JSON.parse(key))
      .then((data) => {
        if (cancelled) return
        setInsights(data)
        setHasFailed(false)
      })
      .catch(() => {
        if (cancelled) return
        setHasFailed(true)
        toast.error('Erro ao buscar os indicadores da área do professor.')
      })
      .finally(() => !cancelled && setIsLoading(false))
    return () => {
      cancelled = true
    }
  }, [key])

  useEffect(() => load(), [load])

  useEffect(() => {
    const { type } = getModelChangeEvent(APP, INSIGHTS_MODEL)
    const onChange = () => load()
    window.addEventListener(type, onChange)
    return () => window.removeEventListener(type, onChange)
  }, [load])

  return { insights, isLoading, hasFailed }
}

const StatTile = ({ icon, label, value, hint, attention, className }) => {
  const classes = useStyles()
  return (
    <Card elevation={3} className={clsx(classes.tile, className)}>
      <div className={classes.tileLabel}>
        <Icon aria-hidden>{icon}</Icon>
        {label}
      </div>
      <p className={clsx(classes.tileValue, attention && classes.attention)}>
        {value}
      </p>
      {hint && <div className={classes.tileHint}>{hint}</div>}
    </Card>
  )
}

const WeeklyAttendanceChart = ({ weeks }) => {
  const theme = useTheme()
  const classes = useStyles()
  const hasData = weeks.some((week) => week.held_sessions > 0)

  if (!hasData)
    return (
      <div className={classes.empty}>
        Nenhuma chamada registrada no período. A frequência aparece aqui
        conforme as chamadas são feitas no app.
      </div>
    )

  const axisLabel = {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontFamily: 'roboto',
  }
  const option = {
    grid: { top: 16, bottom: 28, left: 44, right: 12 },
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const week = weeks[params[0].dataIndex]
        return [
          `Semana de ${moment(week.week_start).format('DD/MM')}`,
          `Frequência: ${formatPercent(week.attendance_rate)}`,
          `Presenças: ${week.presences} de ${week.expected_presences}`,
          `Chamadas: ${week.held_sessions} de ${week.scheduled_sessions} aulas`,
        ].join('<br/>')
      },
    },
    xAxis: {
      type: 'category',
      data: weeks.map((week) => moment(week.week_start).format('DD/MM')),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel,
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      interval: 25,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: {
        lineStyle: { color: theme.palette.text.secondary, opacity: 0.15 },
      },
      axisLabel: { ...axisLabel, formatter: '{value}%' },
    },
    series: [
      {
        name: 'Frequência',
        type: 'bar',
        barMaxWidth: 28,
        data: weeks.map((week) => week.attendance_rate),
        itemStyle: {
          color: theme.palette.primary.main,
          barBorderRadius: [4, 4, 0, 0],
        },
      },
    ],
  }

  return (
    <React.Suspense fallback={<CircularProgress size={20} />}>
      <ReactEcharts option={option} style={{ height: 220 }} />
    </React.Suspense>
  )
}

const PendingRollCalls = ({ pending, onRollCall }) => {
  const classes = useStyles()
  return (
    <Card elevation={3} className={clsx(classes.card, 't-pending-roll-calls')}>
      <h3 className={classes.cardTitle}>
        <Icon aria-hidden>pending_actions</Icon>
        Chamadas pendentes
        {pending.count > 0 && (
          <span className={classes.cardCount}>{pending.count}</span>
        )}
      </h3>
      {pending.items.length ? (
        <>
          {pending.items.map((item) => (
            <div
              key={`${item.activity_class_id}-${item.class_date}`}
              className={clsx(classes.item, 't-pending-roll-call')}
            >
              <div className={classes.itemText}>
                <b>{item.description}</b>
                <span className={classes.muted}>
                  {moment(item.class_date).format('ddd, DD/MM')} ·{' '}
                  {item.start_time}
                </span>
              </div>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                style={{ whiteSpace: 'nowrap', minHeight: 36 }}
                onClick={() => onRollCall(item)}
              >
                Fazer chamada
              </Button>
            </div>
          ))}
          {pending.count > pending.items.length && (
            <p className={clsx(classes.muted, 'mb-0')}>
              E mais {pending.count - pending.items.length} no período.
            </p>
          )}
        </>
      ) : (
        <div className={classes.empty}>
          Nenhuma chamada pendente no período.
        </div>
      )}
    </Card>
  )
}

const AbsentStudents = ({ absent }) => {
  const classes = useStyles()
  return (
    <Card elevation={3} className={clsx(classes.card, 't-absent-students')}>
      <h3 className={classes.cardTitle}>
        <Icon aria-hidden>person_off</Icon>
        Alunos para acompanhar
        {absent.count > 0 && (
          <span className={classes.cardCount}>{absent.count}</span>
        )}
      </h3>
      {absent.items.length ? (
        <>
          {absent.items.map((item) => (
            <div
              key={`${item.user_id}-${item.activity_class_id}`}
              className={clsx(classes.item, 't-absent-student')}
            >
              <div className={classes.itemText}>
                <b>
                  <Link to={`/user/details/${item.user_id}`}>{item.name}</Link>
                </b>
                <span className={classes.muted}>
                  {item.description} ·{' '}
                  {item.last_presence
                    ? `última presença em ${formatDate(item.last_presence)}`
                    : 'sem presença recente'}
                </span>
              </div>
              <span className={clsx(classes.attention, classes.streak)}>
                {item.missed_in_a_row} faltas seguidas
              </span>
            </div>
          ))}
          {absent.count > absent.items.length && (
            <p className={clsx(classes.muted, 'mb-0')}>
              E mais {absent.count - absent.items.length} alunos.
            </p>
          )}
        </>
      ) : (
        <div className={classes.empty}>
          Nenhum aluno com {absent.streak} ou mais faltas seguidas.
        </div>
      )}
    </Card>
  )
}

const TeacherInsights = ({
  insights,
  isLoading,
  hasFailed,
  period,
  onPeriodChange,
  teacherChip,
  onRollCall,
}) => {
  const classes = useStyles()
  const isMobile = useIsMobile()

  const periodButtons = (
    <ButtonGroup
      size="small"
      color="primary"
      aria-label="Período dos indicadores"
      className={clsx(classes.periods, isMobile && classes.periodsMobile)}
    >
      {PERIODS.map((days) => (
        <Button
          key={days}
          variant={period === days ? 'contained' : 'outlined'}
          aria-pressed={period === days}
          onClick={() => onPeriodChange(days)}
        >
          {days} dias
        </Button>
      ))}
    </ButtonGroup>
  )

  let content
  if (!insights && isLoading) {
    content = (
      <div className="text-center py-8">
        <CircularProgress />
      </div>
    )
  } else if (!insights) {
    content = (
      <div className={classes.empty}>
        {hasFailed
          ? 'Não foi possível carregar os indicadores.'
          : 'Nenhum indicador disponível.'}
      </div>
    )
  } else {
    const { summary, sessions, enrollments, weekly } = insights
    content = (
      <div className={clsx(classes.loading, isLoading && classes.isLoading)}>
        <Grid container spacing={2}>
          <Grid item xs={6} md={4} lg={2}>
            <StatTile
              className="t-stat-classes"
              icon="groups"
              label="Turmas ativas"
              value={summary.active_classes_count}
              hint={`${summary.weekly_sessions} aulas/semana · ${formatHours(
                summary.weekly_minutes
              )}`}
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatTile
              className="t-stat-students"
              icon="person"
              label="Alunos ativos"
              value={summary.active_students_count}
              hint={`+${enrollments.new_count} entradas · −${enrollments.cancelled_count} saídas`}
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatTile
              className="t-stat-occupation"
              icon="event_seat"
              label="Ocupação média"
              value={formatPercent(summary.average_occupation)}
              hint={
                summary.total_seats
                  ? `${summary.occupied_seats} de ${summary.total_seats} vagas`
                  : 'Sem turmas fixas com limite de vagas'
              }
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatTile
              className="t-stat-attendance"
              icon="how_to_reg"
              label="Frequência média"
              value={formatPercent(sessions.attendance_rate)}
              hint={
                sessions.expected_presences
                  ? `${sessions.presences} presenças de ${sessions.expected_presences}`
                  : 'Sem chamadas no período'
              }
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatTile
              className="t-stat-roll-calls"
              icon="fact_check"
              label="Chamadas feitas"
              value={formatPercent(sessions.roll_call_rate)}
              hint={`${sessions.held_sessions} de ${sessions.scheduled_sessions} aulas`}
            />
          </Grid>
          <Grid item xs={6} md={4} lg={2}>
            <StatTile
              className="t-stat-absent"
              icon="person_off"
              label="Alunos em alerta"
              value={insights.absent_students.count}
              attention={insights.absent_students.count > 0}
              hint={`${insights.absent_students.streak}+ faltas seguidas`}
            />
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card elevation={3} className={classes.card}>
              <h3 className={classes.cardTitle}>
                <Icon aria-hidden>insights</Icon>
                Frequência por semana
              </h3>
              <WeeklyAttendanceChart weeks={weekly} />
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <PendingRollCalls
              pending={insights.pending_roll_calls}
              onRollCall={onRollCall}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <AbsentStudents absent={insights.absent_students} />
          </Grid>
        </Grid>
      </div>
    )
  }

  return (
    <div className="mb-6 t-teacher-insights">
      <div className={classes.toolbar}>
        <h2 className={classes.sectionTitle}>Indicadores</h2>
        {teacherChip}
        {periodButtons}
      </div>
      {content}
    </div>
  )
}

const ClassCell = ({ row }) => {
  const classes = useStyles()
  return (
    <div className={classes.classCell}>
      <b className="text-primary">
        <Link
          to={`/activity-class-details/${row.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {row.description}
        </Link>
      </b>
      <div className={classes.muted}>
        {[row.activity_modality, row.legacy_id && `Id legado ${row.legacy_id}`]
          .filter(Boolean)
          .join(' · ')}
      </div>
    </div>
  )
}

const NextSessionCell = ({ row }) => {
  const classes = useStyles()
  return (
    <span
      className={clsx(
        classes.nextSession,
        row.is_in_progress && classes.inProgress,
        !row.next_session && classes.muted
      )}
    >
      {formatNextSession(row)}
    </span>
  )
}

const LastRollCallCell = ({ row }) => {
  const classes = useStyles()
  return (
    <>
      {row.last_roll_call ? formatDate(row.last_roll_call) : 'Nunca'}
      {row.pending_roll_calls > 0 && (
        <div className={classes.pendingCount}>
          {row.pending_roll_calls}{' '}
          {row.pending_roll_calls === 1 ? 'pendente' : 'pendentes'} (30 dias)
        </div>
      )}
    </>
  )
}

/**
 * The "Área do professor" tab of the Modalidades page (behind
 * `teacher_area`): indicators over the teacher's classes for a period —
 * weekly load, students, occupancy, attendance rate and its weekly trend,
 * roll calls done and pending, students on an absence streak — above every
 * class of theirs, the running ones first by their next session, searchable.
 * It's the logged in user's area; managers and administrators can pick
 * another teacher. The selected modality `category` scopes all of it.
 */
const TeacherArea = ({ category }) => {
  const history = useHistory()
  const location = useLocation()
  const userInfo = useUserInfo()
  const showModal = useModal()
  const canPickTeacher = useIsUser([USER_TYPE.MANAGER, USER_TYPE.ADMINISTRATOR])

  const params = new URLSearchParams(location.search)
  const pickedTeacher = canPickTeacher
    ? params.get(TEACHER_PARAM) || undefined
    : undefined
  const teacher = pickedTeacher || (userInfo?.id && String(userInfo.id))
  const requestedPeriod = Number(params.get(PERIOD_PARAM))
  const period = PERIODS.includes(requestedPeriod)
    ? requestedPeriod
    : DEFAULT_PERIOD
  const modalityCategory = categoryParam(category)

  const [teacherName, setTeacherName] = useState(null)

  const navigate = (update) => {
    const next = new URLSearchParams(location.search)
    update(next)
    history.replace({ pathname: location.pathname, search: next.toString() })
  }

  const insightsParams = useMemo(
    () => ({
      teacher,
      ...(modalityCategory && { category: modalityCategory }),
      ...periodRange(period),
    }),
    [teacher, modalityCategory, period]
  )
  const { insights, isLoading, hasFailed } = useTeacherInsights(insightsParams)
  const name = insights?.teacher?.name || teacherName

  const openRollCall = (activityClass, classDate) =>
    showModal({
      title: `Chamada · ${activityClass.description}`,
      children: (
        <ActivityClassRollCall
          classId={activityClass.id}
          initialDate={classDate}
        />
      ),
    })

  const teacherDefinition = {
    key: TEACHER_PARAM,
    label: 'Professor',
    icon: 'person',
    type: FILTER_TYPE.MODEL,
    djangoApp: 'users',
    djangoModel: 'user',
  }

  const teacherChip = canPickTeacher && (
    <FilterChip
      definition={teacherDefinition}
      value={
        pickedTeacher
          ? {
              values: [pickedTeacher],
              labels: { [pickedTeacher]: name || '…' },
            }
          : undefined
      }
      onChange={(value) =>
        navigate((next) => {
          next.delete(SEARCH_PARAM)
          next.delete(PAGE_PARAM)
          const [id] = value?.values || []
          setTeacherName(id ? value.labels[id] : null)
          if (id && id !== String(userInfo?.id)) next.set(TEACHER_PARAM, id)
          else next.delete(TEACHER_PARAM)
        })
      }
    />
  )

  if (!teacher) return null

  const isOwnArea = !pickedTeacher

  return (
    <ModelListPage
      // A new teacher or category starts the list over.
      key={`${teacher}-${modalityCategory || 'all'}`}
      djangoApp={APP}
      djangoModel={CLASSES_MODEL}
      title={isOwnArea ? 'Minhas turmas' : `Turmas de ${name || 'professor'}`}
      countLabel={{ singular: 'turma', plural: 'turmas' }}
      baseParams={{
        teacher,
        ...(modalityCategory && { category: modalityCategory }),
      }}
      searchPlaceholder="Buscar turma, modalidade ou espaço…"
      emptyStateText={
        isOwnArea
          ? 'Nenhuma turma encontrada. As turmas em que você é professor aparecem aqui.'
          : 'Nenhuma turma encontrada para este professor.'
      }
      filters={[
        {
          key: 'is_active',
          label: 'Situação',
          icon: 'toggle_on',
          type: FILTER_TYPE.BOOLEAN,
          trueLabel: 'Em andamento',
          falseLabel: 'Encerradas ou inativas',
        },
      ]}
      showAdd={false}
      showEdit={false}
      showDelete={false}
      summary={() => (
        <TeacherInsights
          insights={insights}
          isLoading={isLoading}
          hasFailed={hasFailed}
          period={period}
          onPeriodChange={(days) =>
            navigate((next) => {
              if (days === DEFAULT_PERIOD) next.delete(PERIOD_PARAM)
              else next.set(PERIOD_PARAM, String(days))
            })
          }
          teacherChip={teacherChip}
          onRollCall={(item) =>
            openRollCall(
              { id: item.activity_class_id, description: item.description },
              item.class_date
            )
          }
        />
      )}
      rowActions={(row) => [
        {
          key: 'details',
          label: 'Ver turma',
          icon: 'groups',
          to: `/activity-class-details/${row.id}`,
        },
        {
          key: 'roll-call',
          label: 'Fazer chamada',
          icon: 'fact_check',
          onClick: () => openRollCall(row),
        },
      ]}
      columns={[
        {
          key: 'description',
          header: 'Turma',
          mobile: 'primary',
          render: (row) => <ClassCell row={row} />,
        },
        {
          key: 'next_session',
          header: 'Próxima aula',
          mobile: 'aside',
          render: (row) => <NextSessionCell row={row} />,
        },
        {
          key: 'schedule',
          header: 'Horário',
          render: formatSchedule,
        },
        {
          key: 'physical_space',
          header: 'Espaço',
          render: (row) => row.physical_space,
        },
        {
          key: 'students',
          header: 'Alunos',
          align: 'right',
          render: (row) =>
            row.capacity
              ? `${row.enrolled_count}/${row.capacity}`
              : row.enrolled_count,
        },
        {
          key: 'attendance_rate',
          header: 'Frequência (30 dias)',
          align: 'right',
          render: (row) => formatPercent(row.attendance_rate),
        },
        {
          key: 'last_roll_call',
          header: 'Última chamada',
          render: (row) => <LastRollCallCell row={row} />,
        },
      ]}
    />
  )
}

export default TeacherArea
